/**
 * Mock nutrition service.
 * Mirrors the real nutrition.service.js API with in-memory data.
 */
import { MOCK_MEALS, MOCK_NUTRITION_GOALS } from '../../data/mock/mock-nutrition.data';
import { mockDelay, mockLog, mockId } from './mock-delay';

// In-memory state
let meals = [...MOCK_MEALS];
let nutritionGoals = { ...MOCK_NUTRITION_GOALS };

const isSameDay = (d1, d2) => {
  const a = new Date(d1);
  const b = new Date(d2);
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

export const logMeal = async (userId, mealData) => {
  mockLog('NutritionService', 'logMeal', mealData.title);
  await mockDelay();
  const mealDate = mealData.date ? new Date(mealData.date) : new Date();
  mealDate.setHours(12, 0, 0, 0);
  const meal = {
    id: `mock_meal_${mockId()}`,
    title: mealData.title || 'Meal',
    mealType: mealData.mealType || 'snack',
    calories: mealData.calories || 0,
    macros: {
      protein: mealData.macros?.protein || 0,
      carbs: mealData.macros?.carbs || 0,
      fat: mealData.macros?.fat || 0,
      fiber: mealData.macros?.fiber || 0,
    },
    ingredients: mealData.ingredients || [],
    recipe: mealData.recipe || null,
    imageUrl: mealData.imageUrl || null,
    date: mealDate.toISOString(),
    sharedAsPostId: mealData.sharedAsPostId || null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  meals = [meal, ...meals];
  return meal;
};

export const getMealsByDate = async (userId, date) => {
  mockLog('NutritionService', 'getMealsByDate', date);
  await mockDelay();
  return meals
    .filter((m) => isSameDay(m.date, date))
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

export const getMealsByDateRange = async (userId, startDate, endDate) => {
  mockLog('NutritionService', 'getMealsByDateRange', { startDate, endDate });
  await mockDelay();
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);
  return meals
    .filter((m) => {
      const d = new Date(m.date);
      return d >= start && d <= end;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};

export const getMealById = async (userId, mealId) => {
  mockLog('NutritionService', 'getMealById', mealId);
  await mockDelay();
  return meals.find((m) => m.id === mealId) || null;
};

export const updateMeal = async (userId, mealId, updates) => {
  mockLog('NutritionService', 'updateMeal', mealId);
  await mockDelay();
  const idx = meals.findIndex((m) => m.id === mealId);
  if (idx !== -1) {
    meals[idx] = { ...meals[idx], ...updates, updatedAt: new Date().toISOString() };
    return meals[idx];
  }
  return null;
};

export const deleteMeal = async (userId, mealId) => {
  mockLog('NutritionService', 'deleteMeal', mealId);
  await mockDelay();
  meals = meals.filter((m) => m.id !== mealId);
  return mealId;
};

export const getDailyTotals = async (userId, date) => {
  mockLog('NutritionService', 'getDailyTotals', date);
  await mockDelay();
  const dayMeals = meals.filter((m) => isSameDay(m.date, date));
  const totals = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, mealCount: dayMeals.length };
  dayMeals.forEach((m) => {
    totals.calories += m.calories || 0;
    totals.protein += m.macros?.protein || 0;
    totals.carbs += m.macros?.carbs || 0;
    totals.fat += m.macros?.fat || 0;
    totals.fiber += m.macros?.fiber || 0;
  });
  return totals;
};

export const setNutritionGoals = async (userId, goals) => {
  mockLog('NutritionService', 'setNutritionGoals', goals);
  await mockDelay();
  nutritionGoals = {
    dailyCalories: goals.dailyCalories || 2200,
    macroTargets: {
      protein: goals.macroTargets?.protein || 150,
      carbs: goals.macroTargets?.carbs || 250,
      fat: goals.macroTargets?.fat || 70,
      fiber: goals.macroTargets?.fiber || 30,
    },
    updatedAt: new Date().toISOString(),
  };
  return nutritionGoals;
};

export const getNutritionGoals = async (userId) => {
  mockLog('NutritionService', 'getNutritionGoals', userId);
  await mockDelay();
  return { ...nutritionGoals };
};
